import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Radios, Field, Button, Spacer } from '@ontruck/orc';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@ontruck/ontruck-react-components/lib/Modal';
import DocsContext from 'containers/Docs/context/DocsContext';
import * as http from 'utils/http';
import Select from 'react-select-v3';
import { reactSelectStyles } from 'utils/style';

const t = {
  createCarrier: 'Create carrier',
  mainInfo: 'Main Information',
  labels: {
    name: 'Carrier name',
    fleet: 'Fleet',
    self_employee: 'Self Employee',
    contact_name: 'Contact person',
    phone_number: 'Phone number',
    email: 'Comunications email',
    action: 'Create carrier',
    ontruckCompany: 'Ontruck Company',
  },
  contact: 'Contact',
  error: 'Required Field',
  errorType: 'Please you need to select a type',
  errorSelect: 'Please you need to select an option',
};

const countryOptions = [
  { label: 'NinjaTruck S.L - Spain and International', value: 'ES' },
  { label: 'Ontruck SAS - France', value: 'FR' },
  { label: 'Ontruck LTD - United Kingdom', value: 'UK' },
  { label: 'Ontruck BV - Nederlands', value: 'NL' },
];

const DocsCreateCarrier = ({ className, show, onClose }) => {
  const [carrier, setCarrier] = useState({
    name: '',
    is_autonomous: '',
    contact_name: '',
    phone: '',
    email: '',
    country: '',
  });
  const { postCreateCarrier } = useContext(DocsContext);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFieldChange = useCallback(
    (field, value) => {
      setCarrier({
        ...carrier,
        [field]: value,
      });
      setError(false);
    },
    [carrier]
  );

  const validateNotNull = useCallback(() => {
    setError(false);
    let isValid = true;
    Object.keys(carrier).forEach((item) => {
      if (carrier[item] === '') {
        isValid = false;
        setError(true);
      }
    });
    return isValid;
  }, [carrier]);

  const castBoolean = useCallback((item) => {
    return item === 'true';
  }, []);

  const resetCarrier = useCallback(() => {
    setCarrier({
      name: '',
      is_autonomous: '',
      contact_name: '',
      phone: '',
      email: '',
    });
  }, []);

  const handleSubmitAndValidate = useCallback(async () => {
    setSubmitted(true);
    try {
      if (validateNotNull()) {
        setLoading(true);
        setServerError(null);
        await postCreateCarrier({ ...carrier, is_autonomous: castBoolean(carrier.is_autonomous) });
        setSubmitted(false);
        resetCarrier();
        onClose();
      }
    } catch (err) {
      const errorMessage = await http.getError(err);
      console.error(errorMessage);
      setServerError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [carrier, castBoolean, onClose, postCreateCarrier, resetCarrier, validateNotNull]);

  const getCountryOption = () => {
    return countryOptions.find((country) => country.value === carrier.country) || null;
  };

  return (
    <Modal show={show} size="L" onClose={onClose}>
      <div className={className} data-testid="docs-create-carrier">
        <div className="docs-create-carrier">
          <ModalHeader>{t.createCarrier}</ModalHeader>
          <ModalBody noPadding light>
            <div className="docs-create-carrier__body">
              <div className="docs-create-carrier__section">
                <h2 className="docs-create-carrier__section-title">{t.mainInfo}</h2>
                <Field
                  id="name"
                  name="name"
                  className="docs-create-carrier__field docs-create-carrier__field--large"
                  label={t.labels.name}
                  value={carrier.name}
                  onChange={(e, { name, value }) => handleFieldChange(name, value)}
                  error={submitted && carrier.name === '' ? t.error : ''}
                />
                <Spacer mBottom="S">
                  <Radios
                    girded
                    size="M"
                    name="is_autonomous"
                    value={carrier.is_autonomous}
                    items={[
                      { value: 'false', label: t.labels.fleet },
                      { value: 'true', label: t.labels.self_employee },
                    ]}
                    onChange={(e, { name, value }) => handleFieldChange(name, value)}
                  />
                  {submitted && carrier.is_autonomous === '' && (
                    <div className="docs-create-carrier__radio-error">{t.errorType}</div>
                  )}
                </Spacer>
                <Select
                  containerWidth="auto"
                  placeholder={t.labels.ontruckCompany}
                  name="country"
                  className="docs-create-carrier__field docs-create-carrier__field--large"
                  value={getCountryOption()}
                  options={countryOptions}
                  onChange={(selectedOption) => handleFieldChange('country', selectedOption.value)}
                  styles={reactSelectStyles}
                />
                {submitted && carrier.country === '' && (
                  <div className="docs-create-carrier__radio-error">{t.errorSelect}</div>
                )}
              </div>
              <div className="docs-create-carrier__section">
                <h2 className="docs-create-carrier__section-title">{t.contact}</h2>
                <div className="docs-create-carrier__inline-fields">
                  <Field
                    id="contact_name"
                    name="contact_name"
                    className="docs-create-carrier__field"
                    label={t.labels.contact_name}
                    value={carrier.contact_name}
                    onChange={(e, { name, value }) => handleFieldChange(name, value)}
                    error={submitted && carrier.contact_name === '' ? t.error : ''}
                  />
                  <Field
                    id="phone"
                    name="phone"
                    className="docs-create-carrier__field"
                    label={t.labels.phone_number}
                    value={carrier.phone}
                    onChange={(e, { name, value }) => handleFieldChange(name, value)}
                    error={submitted && carrier.phone === '' ? t.error : ''}
                  />
                  <Field
                    id="email"
                    name="email"
                    className="docs-create-carrier__field"
                    label={t.labels.email}
                    value={carrier.email}
                    onChange={(e, { name, value }) => handleFieldChange(name, value)}
                    error={submitted && carrier.email === '' ? t.error : ''}
                  />
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter noPadding light className="docs-create-carrier__footer">
            {serverError && (
              <div className="docs-create-carrier__footer-error">
                {Object.keys(serverError).map((error) => (
                  <div>
                    <strong>{error}</strong>: {serverError[error]}
                  </div>
                ))}
              </div>
            )}
            <div className="docs-create-carrier__footer">
              <Button
                data-testid="docs-create-carrier__submit"
                onClick={handleSubmitAndValidate}
                disabled={error || loading}
                text={t.labels.action}
              />
            </div>
          </ModalFooter>
        </div>
      </div>
    </Modal>
  );
};

DocsCreateCarrier.defaultProps = {
  className: '',
  show: false,
};

DocsCreateCarrier.propTypes = {
  className: PropTypes.string,
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

export default DocsCreateCarrier;
